/**
 * CopilotKit context provider
 *
 * Provides CopilotKit AI assistant functionality to the component tree.
 * Wraps the CopilotKit provider with proper runtime URL configuration
 * and error handling.
 *
 * IMPORTANT: CopilotKit requires authentication because it immediately tries
 * to connect to the runtime endpoint on mount. If the user is not authenticated,
 * this provider renders children without CopilotKit context.
 *
 * Per Phase 3.4.10 AI Check-in UX Improvements:
 * - Error handling via onError callback
 * - Error state tracking for UI feedback
 *
 * @see https://docs.copilotkit.ai/reference/components/CopilotKit
 */
import { CopilotKit } from "@copilotkit/react-core";
import "@copilotkit/react-ui/styles.css";
import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./auth-provider";
import { API_URL } from "@/env";
import {
  createErrorState,
  createInitialRetryState,
  clearErrorState,
  resetRetryState,
  startRetryAttempt,
  completeRetryAttempt,
  canRetry,
  type CopilotErrorState,
  type RetryState,
  type CopilotErrorEvent,
} from "@/components/copilot/copilot-error-utils";

// ============================================================================
// Constants
// ============================================================================

/**
 * CopilotKit runtime endpoint.
 * Uses API_URL from env (empty string = same-origin via Vite proxy).
 * @see apps/web/src/env.ts
 */
export const COPILOT_RUNTIME_URL = `${API_URL}/api/copilotkit`;

// ============================================================================
// Context
// ============================================================================

/**
 * Copilot error context value
 */
interface CopilotErrorContextValue {
  /** Current error state, null if no error */
  errorState: CopilotErrorState | null;
  /** Current retry state */
  retryState: RetryState;
  /** Clear the current error */
  clearError: () => void;
  /** Retry the last operation */
  retry: () => void;
  /** Whether CopilotKit is currently available */
  isAvailable: boolean;
}

const CopilotErrorContext = createContext<CopilotErrorContextValue | null>(null);

/**
 * Hook to access CopilotKit error state
 */
export function useCopilotError(): CopilotErrorContextValue {
  const context = useContext(CopilotErrorContext);
  if (!context) {
    // Return a default context when outside provider
    return {
      errorState: null,
      retryState: createInitialRetryState(),
      clearError: () => {},
      retry: () => {},
      isAvailable: false,
    };
  }
  return context;
}

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Check if CopilotKit should be enabled based on authentication state
 *
 * @param isAuthenticated - Whether the user is authenticated
 * @param isLoading - Whether authentication state is loading
 * @returns True if CopilotKit should be initialized
 */
export function shouldEnableCopilot(
  isAuthenticated: boolean,
  isLoading: boolean
): boolean {
  // Only enable CopilotKit when authenticated and auth is not loading
  return isAuthenticated && !isLoading;
}

// ============================================================================
// Provider Component
// ============================================================================

interface CopilotProviderProps {
  children: ReactNode;
}

/**
 * CopilotKit provider component
 *
 * Wraps the application with CopilotKit context for AI assistance.
 * The runtime URL is configured to use the API server's CopilotKit endpoint.
 *
 * Authentication-aware: Only initializes CopilotKit when user is authenticated.
 * This prevents 401 errors on the /api/copilotkit endpoint for unauthenticated users.
 *
 * Features enabled (when authenticated):
 * - Streaming AI responses via Server-Sent Events
 * - Frontend action definitions via useCopilotAction
 * - Readable state via useCopilotReadable
 * - Chat UI components (CopilotSidebar, CopilotPopup, etc.)
 * - Error handling with retry support
 *
 * @example
 * ```tsx
 * <CopilotProvider>
 *   <App />
 * </CopilotProvider>
 * ```
 */
export function CopilotProvider({ children }: CopilotProviderProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const enabled = shouldEnableCopilot(isAuthenticated, isLoading);

  // Error state management
  const [errorState, setErrorState] = useState<CopilotErrorState | null>(null);
  const [retryState, setRetryState] = useState<RetryState>(createInitialRetryState);

  // Handle CopilotKit errors
  const handleError = useCallback((event: CopilotErrorEvent) => {
    // Only process error events
    if (event.type === "error" && event.error) {
      console.error("[CopilotKit Error]", {
        type: event.type,
        timestamp: new Date(event.timestamp).toISOString(),
        context: event.context,
        error: event.error,
      });

      const newErrorState = createErrorState(event.error, retryState.attempts);
      setErrorState(newErrorState);
      setRetryState((prev) => completeRetryAttempt(prev));
    }
  }, [retryState.attempts]);

  // Clear error
  const clearError = useCallback(() => {
    setErrorState(clearErrorState());
    setRetryState(resetRetryState());
  }, []);

  // Retry operation
  const retry = useCallback(() => {
    if (errorState?.isRetryable && canRetry(retryState)) {
      setRetryState((prev) => startRetryAttempt(prev));
      // Clear error to allow CopilotKit to reconnect
      setErrorState(null);
    }
  }, [errorState, retryState]);

  // Error context value
  const errorContextValue: CopilotErrorContextValue = {
    errorState,
    retryState,
    clearError,
    retry,
    isAvailable: enabled && errorState === null,
  };

  // When not authenticated, render children without CopilotKit context
  // This prevents 401 errors from CopilotKit trying to connect to the runtime
  if (!enabled) {
    return (
      <CopilotErrorContext.Provider value={errorContextValue}>
        {children}
      </CopilotErrorContext.Provider>
    );
  }

  return (
    <CopilotErrorContext.Provider value={errorContextValue}>
      <CopilotKit
        runtimeUrl={COPILOT_RUNTIME_URL}
        showDevConsole={import.meta.env.DEV}
        credentials="include"
        onError={handleError}
      >
        {children}
      </CopilotKit>
    </CopilotErrorContext.Provider>
  );
}

// ============================================================================
// Re-exports for convenience
// ============================================================================

// Re-export commonly used hooks for easy access
export { useCopilotAction, useCopilotReadable } from "@copilotkit/react-core";

// Re-export UI components
export { CopilotSidebar, CopilotPopup, CopilotChat } from "@copilotkit/react-ui";
